import { useCallback, useEffect, useRef, useState } from "react";
import { MenuOutlined } from "@ant-design/icons";
import { useTranslation } from "../../i18n";

// 悬浮的快速滚动手柄：一个白色小方块（内含三条横线）。
// 平时隐藏，用户上下滑动时淡入显示，且其纵向位置跟随滚动进度；
// 用户可按住它沿屏幕拖动，从而快速定位到页面任意位置。
const HANDLE_SIZE = 38;
const TRACK_MARGIN = 72;
const SCROLLABLE_THRESHOLD = 120;
const IDLE_HIDE_MS = 1400;

function getScrollMetrics() {
    if (typeof window === "undefined") {
        return { scrollTop: 0, maxScroll: 0 };
    }
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const scrollHeight = Math.max(doc.scrollHeight, document.body.scrollHeight);
    const clientHeight = window.innerHeight || doc.clientHeight;
    const maxScroll = Math.max(scrollHeight - clientHeight, 0);
    return { scrollTop, maxScroll };
}

function getTrackLength() {
    const viewport = typeof window !== "undefined" ? window.innerHeight : 0;
    return Math.max(viewport - TRACK_MARGIN * 2 - HANDLE_SIZE, 1);
}

function ScrollNavigator() {
    const { t } = useTranslation();
    const draggingRef = useRef(false);
    const rafRef = useRef(0);
    const hideTimerRef = useRef(0);
    const [progress, setProgress] = useState(0);
    const [scrollable, setScrollable] = useState(false);
    const [visible, setVisible] = useState(false);

    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = 0;
        }
    }, []);

    const scheduleHide = useCallback(() => {
        clearHideTimer();
        hideTimerRef.current = window.setTimeout(() => {
            if (!draggingRef.current) {
                setVisible(false);
            }
        }, IDLE_HIDE_MS);
    }, [clearHideTimer]);

    const readProgress = useCallback(() => {
        const { scrollTop, maxScroll } = getScrollMetrics();
        setScrollable(maxScroll > SCROLLABLE_THRESHOLD);
        setProgress((prev) => {
            const next = maxScroll > 0 ? scrollTop / maxScroll : 0;
            return Math.abs(prev - next) > 0.0005 ? next : prev;
        });
    }, []);

    useEffect(() => {
        const onScroll = () => {
            if (!rafRef.current) {
                rafRef.current = window.requestAnimationFrame(() => {
                    rafRef.current = 0;
                    readProgress();
                });
            }
            const { maxScroll } = getScrollMetrics();
            if (maxScroll > SCROLLABLE_THRESHOLD) {
                setVisible(true);
                if (!draggingRef.current) {
                    scheduleHide();
                }
            }
        };

        readProgress();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", readProgress);

        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", readProgress);
            clearHideTimer();
            if (rafRef.current) {
                window.cancelAnimationFrame(rafRef.current);
            }
        };
    }, [clearHideTimer, readProgress, scheduleHide]);

    const scrollToClientY = useCallback((clientY) => {
        const { maxScroll } = getScrollMetrics();
        const trackLength = getTrackLength();
        const ratio = (clientY - TRACK_MARGIN - HANDLE_SIZE / 2) / trackLength;
        const clamped = Math.min(Math.max(ratio, 0), 1);
        window.scrollTo({ top: maxScroll * clamped, behavior: "auto" });
    }, []);

    const startDrag = useCallback(
        (event) => {
            event.preventDefault();
            event.stopPropagation();
            draggingRef.current = true;
            clearHideTimer();
            setVisible(true);
            document.body.classList.add("scroll-nav-dragging");

            const move = (moveEvent) => {
                const clientY =
                    moveEvent.touches?.[0]?.clientY ?? moveEvent.clientY ?? 0;
                scrollToClientY(clientY);
            };
            const end = () => {
                draggingRef.current = false;
                document.body.classList.remove("scroll-nav-dragging");
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", end);
                window.removeEventListener("pointercancel", end);
                scheduleHide();
            };

            window.addEventListener("pointermove", move);
            window.addEventListener("pointerup", end);
            window.addEventListener("pointercancel", end);
        },
        [clearHideTimer, scheduleHide, scrollToClientY]
    );

    const handlePointerEnter = useCallback(() => {
        clearHideTimer();
        setVisible(true);
    }, [clearHideTimer]);

    const handlePointerLeave = useCallback(() => {
        if (!draggingRef.current) {
            scheduleHide();
        }
    }, [scheduleHide]);

    if (!scrollable) {
        return null;
    }

    const handleTop = TRACK_MARGIN + progress * getTrackLength();

    return (
        <button
            type="button"
            className={`scroll-nav${visible ? " scroll-nav--visible" : ""}`}
            style={{ top: `${handleTop}px` }}
            onPointerDown={startDrag}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            aria-label={t("common.scrollNav")}
            title={t("common.scrollNav")}
            tabIndex={-1}
        >
            <MenuOutlined />
        </button>
    );
}

export default ScrollNavigator;
